import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'voyager';

import type { TranslationKey } from '@lang';
import { isMissingCopy, useTranslations } from '@lang';
import { modalErrorHide } from '@store/modalError/actions';
import { modalErrorSelectors } from '@store/modalError/selectors';

import { reloadAfterError } from './resetAfterError';
import { useDialog } from './useDialog';

const DIALOG_TOAST_ID = 'notification-dialog';

const GENERIC_CODES = ['global', 'undefined', 'ERR_BAD_REQUEST'];

type NotificationDialogError = { code?: string } & Record<string, unknown>;

export const NotificationDialog = () => {
  const { t } = useTranslations();
  const { show: showDialog, dismiss: dismissDialog } = useDialog();
  const dispatch = useDispatch();
  const error = useSelector(modalErrorSelectors.getModalError) as NotificationDialogError | null;
  const keepPage = useSelector(modalErrorSelectors.getModalErrorKeepPage) as boolean;

  useEffect(() => {
    if (!error) {
      dismissDialog(DIALOG_TOAST_ID);
      return;
    }

    const happened = t('error.global');
    const specific = t(`error.${error.code}` as TranslationKey, { ...error });
    const hasDetail =
      Boolean(error.code) &&
      !GENERIC_CODES.includes(String(error.code)) &&
      !isMissingCopy(specific);

    const advice = t('error.tryAgainOrContact');
    const subtitle = hasDetail ? specific : isMissingCopy(advice) ? undefined : advice;

    showDialog(happened, {
      tone: 'danger',
      id: DIALOG_TOAST_ID,
      subtitle,
      cta: (
        <Button
          variant="primary"
          size="small"
          onClick={() => {
            dispatch(modalErrorHide());
            if (!keepPage) reloadAfterError();
          }}
        >
          {t('modalError.confirm')}
        </Button>
      ),
    });

    return () => dismissDialog(DIALOG_TOAST_ID);
  }, [error, keepPage, dispatch, showDialog, dismissDialog, t]);

  return null;
};
